
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, Loader2, AlertCircle } from 'lucide-react';
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AIInsightGeneratorProps {
  onInsightsGenerated?: (insights: string) => void;
}

export const AIInsightGenerator = ({ onInsightsGenerated }: AIInsightGeneratorProps) => {
  const [inputData, setInputData] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState<string>('');
  const { callAIService, isLoading, response } = useOpenAIService();

  const handleGenerateInsights = async () => {
    if (!inputData.trim() || !industry) {
      toast.error('Please provide both business data and select an industry');
      return;
    }

    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to use AI insights');
        return;
      }

      console.log('Generating insights for:', { industry, dataLength: inputData.length });

      const insights = await callAIService({
        feature: 'insight_generator',
        data: inputData,
        industry,
        userId: user.id,
        interactionType: 'insight_generation'
      });

      onInsightsGenerated?.(insights);
      toast.success('Insights generated successfully!');
    } catch (error) {
      console.error('Failed to generate insights:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate insights';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Brain className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI Insight Generator</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Industry</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Business Data</label>
          <Textarea
            placeholder="Paste your CSV data, CRM data, or describe your business metrics..."
            value={inputData}
            onChange={(e) => setInputData(e.target.value)}
            rows={6}
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <Button 
          onClick={handleGenerateInsights}
          disabled={isLoading || !inputData.trim() || !industry}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Insights...
            </>
          ) : (
            'Generate AI Insights'
          )}
        </Button>

        {response && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">AI Generated Insights:</h4>
            <div className="whitespace-pre-wrap text-sm">{response}</div>
          </div>
        )}
      </div>
    </Card>
  );
};
